// A Nango connection of the group that a store uses. A connection id is unique only within
// its integration, so both identify it.
export interface NangoConnection {
  integration: string
  connectionId: string
  linkedAt?: Date
}

// What the store answered the last time REVER checked the connection's credentials.
export interface NangoConnectionHealth {
  state: 'ok' | 'invalid'
  platformError?: string
  failingSince?: Date
  checkedAt?: Date
  // The store the connection's portals call when the connection now signs in to another, as after a Reconnect.
  portalStore?: string
}

// A Nango connection owned by a group. Assigning it to stores is optional: none is valid.
export interface GroupNangoConnection {
  integration: string
  connectionId: string
  provider: string
  createdAt?: Date
  linkedEcommerceIds: string[]
  // Unset until REVER checks the connection, which it does once a store call is rejected.
  health?: NangoConnectionHealth
  // The store the connection connects, such as a shop name or URL; unset for an integration without one.
  store?: string
}

// What a merchant knows a connection by: its store, or its id when REVER has none for it.
export const connectionLabel = (
  connection: Pick<GroupNangoConnection, 'connectionId' | 'store'>
) => connection.store || connection.connectionId

export const isBroken = (connection: Pick<GroupNangoConnection, 'health'>) =>
  connection.health?.state === 'invalid'

export interface NangoConnectSession {
  token: string
  expiresAt?: Date
}

// The token REVER deploys its Shopify app to a store with. The dashboard never reads the token itself.
export interface ShopifyAppAutomationToken {
  tokenSet: boolean
  expiresAt?: Date
  // REVER deploys its Shopify app to the store: WFP or size suggestions are on.
  required: boolean
}

export type AppAutomationTokenStatus =
  | 'missing'
  | 'no_expiry'
  | 'expired'
  | 'expires'

export function appAutomationTokenStatus(
  token: ShopifyAppAutomationToken,
  now: Date
): AppAutomationTokenStatus {
  if (!token.tokenSet) return 'missing'
  if (!token.expiresAt) return 'no_expiry'
  return token.expiresAt <= now ? 'expired' : 'expires'
}

// The start of the day in UTC, so a token never counts as valid past the day the Shopify Dev Dashboard shows.
export function expiryOfDay(day: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined
  const expiry = new Date(`${day}T00:00:00Z`)
  return Number.isNaN(expiry.getTime()) ? undefined : expiry
}

// Where Integrations lists a connection: the platform the orders live in, or any other app.
export type IntegrationKind = 'platform' | 'app'

// The integrations Connect offers in each section: given no list, Nango offers every integration it has.
export const CONNECTABLE_INTEGRATIONS: Record<IntegrationKind, string[]> = {
  platform: [
    'woocommerce',
    'prestashop',
    // For stores whose server drops the Authorization header: the key goes in the URL.
    'prestashop-query',
    'shopware',
    'magento-bearer',
    'magento-oauth1',
    'commercelayer',
    // A Salesforce portal uses one of each: OCAPI's holds the Business Manager password and its access key.
    'salesforce-scapi',
    'salesforce-slas',
    'salesforce-core',
    'salesforce-ocapi',
    // Last: the picker lists a merchant's own API after the platforms it may run on.
    'custom-v2',
    'custom-v1',
    // Custom v1 stores that take REVER's key in X-rever-api-key instead of X-Auth-Token.
    'custom-v1-rever-key'
  ],
  // None yet: the apps in Nango are gateways and ERPs set up by REVER for one merchant.
  app: []
}

// A merchant can only reconnect or disconnect what Connect lets them connect again.
export const canConnectAgain = ({
  integration
}: Pick<GroupNangoConnection, 'integration'>) =>
  Object.values(CONNECTABLE_INTEGRATIONS).some((integrations) =>
    integrations.includes(integration)
  )

// Platforms connected only by installing REVER's app from the store: each merchant has its own app, which Nango lacks.
export const INSTALLED_PLATFORMS = ['shopify']

// Every platform the picker lists, installed ones first.
export const PLATFORM_INTEGRATIONS = [
  ...INSTALLED_PLATFORMS,
  ...CONNECTABLE_INTEGRATIONS.platform
]

// Integrations that connect the same platform share its logo and its row in the picker.
const PLATFORM_OF: Record<string, string> = {
  'prestashop-query': 'prestashop',
  'magento-bearer': 'magento',
  'magento-oauth1': 'magento',
  'salesforce-scapi': 'salesforce',
  'salesforce-slas': 'salesforce',
  'salesforce-core': 'salesforce',
  'salesforce-ocapi': 'salesforce',
  'salesforce-ocapi-agent': 'salesforce',
  'salesforce-ocapi-basic': 'salesforce',
  'custom-v2': 'custom',
  'custom-v1': 'custom',
  'custom-v1-rever-key': 'custom'
}

export const platformOf = (integration: string) =>
  PLATFORM_OF[integration] ?? integration

export interface PickerPlatform {
  platform: string
  integrations: string[]
}

// The picker lists each platform once, and asks which integration only for one that several connect.
export const PICKER_PLATFORMS: PickerPlatform[] = [
  ...new Set(PLATFORM_INTEGRATIONS.map(platformOf))
].map((platform) => ({
  platform,
  integrations: PLATFORM_INTEGRATIONS.filter(
    (integration) => platformOf(integration) === platform
  )
}))

// The integrations REVER creates in Nango besides the platforms: refund gateways and ERPs.
const REVER_APP_INTEGRATIONS = [
  'klarna',
  'adyen',
  'axerve',
  'boggi-oms',
  'marypaz',
  'alvaromoreno-erp'
]

// Nango providers that are ecommerce platforms, each variant mapped to the platform it connects.
const PLATFORM_PROVIDERS: Record<string, string> = {
  shopify: 'shopify',
  'shopify-oauth': 'shopify',
  'shopify-api-key': 'shopify',
  woocommerce: 'woocommerce',
  prestashop: 'prestashop',
  shopware: 'shopware'
}

// Generic providers (private-api-*) host many integrations, so only the integration tells ours apart.
export function integrationKeyOf(
  connection: Pick<GroupNangoConnection, 'integration' | 'provider'>
): string {
  const { integration } = connection
  const provider = (connection.provider || '').toLowerCase()
  if (
    !provider ||
    provider.startsWith('private-api') ||
    PLATFORM_INTEGRATIONS.includes(integration) ||
    REVER_APP_INTEGRATIONS.includes(integration)
  ) {
    return integration
  }
  return PLATFORM_PROVIDERS[provider] ?? provider
}

const PLATFORM_KEYS = new Set([
  ...PLATFORM_INTEGRATIONS,
  ...Object.values(PLATFORM_PROVIDERS)
])

// Platforms REVER imports from the stores' saved credentials, which merchants don't connect. magento is Magento's
// access token on Nango's adobe-commerce template, until its stores move to magento-bearer.
const IMPORTED_PLATFORM_INTEGRATIONS = [
  'magento',
  // Access keys imported before OCAPI's connection held them.
  'salesforce-ocapi-agent',
  'salesforce-ocapi-basic',
  'vtex',
  'logicommerce',
  'own-platform',
  'mirakl',
  'tiktok-shop'
]

// Tokens a store's platform connection has no field for, each imported on a connection of its own.
const STORE_TOKEN_INTEGRATIONS = [
  'shopify-storefront',
  'shopify-app-automation',
  'shopware-store-api'
]

// A store's token is part of that store, so Integrations lists it nowhere.
export function integrationKindOf(
  connection: Pick<GroupNangoConnection, 'integration' | 'provider'>
): IntegrationKind | undefined {
  const { integration } = connection
  if (STORE_TOKEN_INTEGRATIONS.includes(integration)) return undefined
  if (IMPORTED_PLATFORM_INTEGRATIONS.includes(integration)) return 'platform'
  return PLATFORM_KEYS.has(integrationKeyOf(connection)) ? 'platform' : 'app'
}

// Each platform's integrations, so a portal on one can switch to another of its connections, whichever variant.
const ASSIGNABLE_INTEGRATIONS: Record<string, string[]> = {
  SHOPIFY: ['shopify'],
  WOOCOMMERCE: ['woocommerce'],
  SHOPWARE: ['shopware'],
  PRESTASHOP: ['prestashop', 'prestashop-query'],
  MAGENTO_V2: ['magento-bearer', 'magento-oauth1'],
  CUSTOM_GENERIC: ['custom-v1', 'custom-v1-rever-key'],
  CUSTOM_INTEGRATION_V2: ['custom-v2'],
  COMMERCELAYER: ['commercelayer'],
  SALESFORCE: [
    'salesforce-scapi',
    'salesforce-slas',
    'salesforce-core',
    'salesforce-ocapi'
  ]
}

export function assignableIntegrationsOf(platform?: string): string[] {
  return (platform && ASSIGNABLE_INTEGRATIONS[platform]) || []
}
